// src/config/sessionId.js
//
// A stable, per-browser identifier sent as `x-session-id` so the backend can list
// "where am I signed in?" and flag a sign-in from a device it hasn't seen (S3/S6).
//
// ── This is NOT a credential. ───────────────────────────────────────────────
// It carries no authority whatsoever: the backend only ever uses it as a key for
// an observability record, always alongside the verified Firebase token, and it
// refuses a session id already claimed by a different user. Nothing in the auth
// path reads it. Treat it as a device nickname, not a secret — which is why it
// lives in plain localStorage and is generated client-side.
//
// It's deliberately per-BROWSER (localStorage) rather than per-tab
// (sessionStorage): the user thinks in devices, and a new row appearing every
// time they open a tab would make the device list useless.

const STORAGE_KEY = 'qdex_session_id';

function generate() {
    // crypto.randomUUID is unavailable on http:// origins in some browsers, so
    // fall back rather than throwing — a missing id degrades to "not listed",
    // never to a broken sign-in.
    try {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    } catch {
        /* fall through */
    }
    return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

let cached = null;

/**
 * The current browser's session id, creating one on first use.
 * Returns null if storage is unavailable (private mode, blocked cookies) — the
 * caller simply omits the header in that case.
 */
export function getSessionId() {
    if (cached) return cached;
    try {
        let id = localStorage.getItem(STORAGE_KEY);
        if (!id) {
            id = generate();
            localStorage.setItem(STORAGE_KEY, id);
        }
        cached = id;
        return id;
    } catch {
        return null;
    }
}

/**
 * Forget this browser's identity, so the next sign-in registers as a new device.
 * Called after "sign out everywhere" — the old row is revoked, and continuing to
 * present its id would resurrect it.
 */
export function resetSessionId() {
    cached = null;
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* nothing to clear */
    }
}
